import { useMemo } from 'react';

import { ALWAYS_HIDDEN, ALWAYS_SHOWN, NUTRIENT_ORDER, NutrientKey } from './nutrient-config';

export type NutrientItem = {
  label: NutrientKey;
  value: number;
};

/**
 * Centralizes the business rules for which nutrients to display and in what order.
 *
 * Both the Nutrition Explorer (static CMS data) and the Menu PDP must use this hook
 * so that filtering, ordering, and fallback logic live in one place.
 *
 * @param rawNutrients - A flat record of nutrient key → numeric value.
 *   Consumers are responsible for flattening their data source into this shape.
 *   For example, the Menu PDP must merge `calories` from `item.nutrition.calories.def`
 *   into this record before passing it here.
 * @param overrideHidden - Optional per-key visibility overrides (e.g. from LaunchDarkly).
 *   When a key maps to `true`, that nutrient is hidden.
 */
export function useNutrientItems(
  rawNutrients: Record<string, number | null | undefined>,
  overrideHidden?: Record<string, boolean>
): NutrientItem[] {
  return useMemo(() => {
    return NUTRIENT_ORDER.filter(key => {
      if (ALWAYS_HIDDEN.has(key)) {
        return false;
      }
      if (overrideHidden?.[key]) {
        return false;
      }
      if (ALWAYS_SHOWN.has(key)) {
        return true;
      }
      return rawNutrients[key] != null;
    }).map(key => ({
      label: key,
      value: rawNutrients[key] ?? 0,
    }));
  }, [rawNutrients, overrideHidden]);
}
